import { ref, computed, watch, onMounted, shallowRef } from 'vue';
import { useComponentI18n } from '@rdrive/i18n';
import { useFilterContextRefresh } from '@rdrive/data-filters';
import { useChartTheme } from '@rdrive/theme';
import messages from './messages/index.js';
import { downloadChartAsPNG } from '@rdrive/helper-chart-export';
import { formatBucketLabel, bucketStartDate } from '../../helpers/timeBuckets.js';

// The two modes, as a table. Everything that differs between "per-bucket counts"
// and "running totals" is here; the rest of the file is one code path.
//
//   defaultGroupBy  the bucket the chart starts on when `groupBy` is not bound
//   sortBands       how the colour bands stack (ascending / descending by `sort`)
//   boundaryGap     xAxis.boundaryGap; `null` = leave it at the ECharts default
//   axisPointer     the tooltip's axis pointer
//   overlay         whether a load shows the blocking progress overlay
//   series          the per-band ECharts series, given its resolved colour
const MODES = {
  periodic: {
    defaultGroupBy: 'week',
    sortBands: (a, b) => a.sort - b.sort,
    boundaryGap: null,
    axisPointer: 'shadow',
    overlay: false,
    series: (color) => ({
      type: 'line',
      stack: 'Total',
      smooth: true,
      showSymbol: false,
      lineStyle: { color, width: 2 },
      areaStyle: { color, opacity: 0.6 },
      emphasis: { focus: 'series' },
    }),
  },
  cumulative: {
    defaultGroupBy: 'month',
    sortBands: (a, b) => b.sort - a.sort,
    boundaryGap: false,
    axisPointer: 'cross',
    overlay: true,
    series: (color) => ({
      type: 'line',
      stack: 'Total',
      stackStrategy: 'all',
      smooth: false,
      itemStyle: { color },
      lineStyle: { color },
      areaStyle: { opacity: 0.7 },
      emphasis: { focus: 'series', blurScope: 'coordinateSystem' },
    }),
  },
};

// Opt-in overrides of the table above (issue-analysis, IA_ABSORPTION_PLAN Phase 4 step 12c).
// Unset, every band is the mode's own `series` and the bands stack in the mode's `sortBands`
// order — the path project-overview takes.
//
//   seriesType="bar"  stacked bars instead of lines (issue-analysis's velocity), meant for
//                     mode="periodic"
//   bandOrder         'asc' | 'desc' — the stacking order, overriding the mode's
const BAR_SERIES = (color) => ({
  type: 'bar',
  stack: 'Total',
  barMaxWidth: '50%',
  itemStyle: { color },
  emphasis: { focus: 'series' },
});
const BAND_ORDER = {
  asc: (a, b) => a.sort - b.sort,
  desc: (a, b) => b.sort - a.sort,
};

// Opt-ins of asset-management's Inspection Progress (AM_ABSORPTION_PLAN step D4) — its three
// running totals (Total Scope, Planned, Inspected) are separate measures that overlap, not parts of
// a whole. Unset (`stacked` true, the rest false) nothing below changes the option.
//
//   stacked=false  every band is its own line over a faint area (AM's series VERBATIM), or with
//                  seriesType="bar" its own bar beside the others; and the tooltip lists each band's
//                  plotted value at the bucket — no total (the values do not add up), no
//                  fetchTooltip (there is nothing to break down). A null `value` is a gap.
//   showLegend     a legend above the plot (AM's)
//   todayMarker    a dashed line labelled t('today') on the bucket holding today (AM's markLine)
//   shadeFuture    a faint shade from that bucket to the last one (AM's markArea)
const OVERLAY_SERIES = (color) => ({
  type: 'line',
  smooth: true,
  showSymbol: false,
  connectNulls: false,
  lineStyle: { width: 2, color },
  itemStyle: { color },
  areaStyle: { color, opacity: 0.1 },
});
const SIDE_BY_SIDE_BAR_SERIES = (color) => ({
  type: 'bar',
  barMaxWidth: '50%',
  itemStyle: { color },
  emphasis: { focus: 'series' },
});
// "Today" as DuckDB-WASM's CURRENT_DATE sees it — the UTC calendar date (no ICU: its CURRENT_DATE
// ignores the TimeZone setting) — so the marker sits on the last bucket a config's SQL counts as
// past; also asset-management's own `new Date().toISOString().slice(0, 10)`.
const utcToday = () => new Date().toISOString().slice(0, 10);

const TOOLTIP_DEBOUNCE_MS = 300;
const TOOLTIP_WRAPPER = 'max-height: 300px; overflow-y: auto;';

export default (props, { emit }) => {
  // LOCAL composer with the warn flags off (see @rdrive/i18n): the nine keys this
  // component invents come from ./messages; `title`/`infoText` arrive translated.
  const i18n = useComponentI18n(messages);
  const { t } = i18n;

  // THE colour source — no `chartFontColor` prop. A theme toggle re-evaluates this
  // computed, and the watch at the bottom repaints without a reload.
  const theme = useChartTheme();

  const modeSpec = () => MODES[props.mode] ?? MODES.periodic;
  const bandSeries = (spec, color) => {
    if (props.seriesType === 'bar') return props.stacked ? BAR_SERIES(color) : SIDE_BY_SIDE_BAR_SERIES(color);
    return props.stacked ? spec.series(color) : OVERLAY_SERIES(color);
  };
  const bandSort = (spec) => BAND_ORDER[props.bandOrder] ?? spec.sortBands;

  const chartRef = ref(null);
  const chartOption = shallowRef({});
  // shallowRef, not ref: the series objects go straight into the ECharts option, and a
  // deep ref would hand ECharts reactive proxies of every data point. Nothing watches it —
  // `updateChartOption()` is called explicitly after each assignment.
  const chartData = shallowRef({ timeData: [], seriesData: [] });

  // Zoom state
  const isZoomed = ref(false);
  const zoomStart = ref(0);
  const zoomEnd = ref(100);

  // Load state. `pendingReload` is the re-entrancy queue (CHART_GENERALIZATION §8 R-B):
  // a filter tick arriving mid-load used to be DROPPED by the burndown's
  // `if (isLoading === false)` guard; it is now replayed once the load settles.
  const isLoading = ref(false);
  let pendingReload = false;
  const showLoadingOverlay = computed(() => isLoading.value && modeSpec().overlay);

  // Per-bucket tooltip cache, replaced wholesale on every reload
  const tooltipCache = ref(new Map());
  let tooltipDebounceTimer = null;
  // The bucket the pointer is on right now (set by every formatter call) and the last
  // pointer position in chart pixels. Both are plain lets: nothing in the template reads
  // them. The bucket is compared BY VALUE — ECharts hands the formatter a fresh `params`
  // array on every call, so the old SFCs' identity check (`=== params`) never matched and
  // their showTip re-dispatch never fired; the breakdown only appeared once the mouse
  // moved again. The re-dispatch below uses the pointer position, which works for an
  // axis-triggered tooltip regardless of which series sits under the cursor.
  let currentTooltipKey = null;
  let lastPointer = null;

  // ---------------------------------------------------------------- bucket switching
  const groupByRef = ref(props.groupBy ?? modeSpec().defaultGroupBy);
  watch(
    () => props.groupBy,
    (v) => { if (v && v !== groupByRef.value) groupByRef.value = v; },
  );
  const selectedGroupBy = computed({
    get: () => groupByRef.value,
    set: (v) => {
      if (!v || v === groupByRef.value) return;
      groupByRef.value = v;
      emit('update:groupBy', v);
    },
  });

  const formatAxisLabel = (timeKey) =>
    formatBucketLabel(timeKey, groupByRef.value, {
      weekLabel: t('week'),
      locale: i18n.locale.value,
    });

  // ---------------------------------------------------------------- tooltip
  const fetchTooltipData = async (timeKey) => {
    if (tooltipCache.value.has(timeKey)) return tooltipCache.value.get(timeKey);

    try {
      const rows = await props.fetchTooltip(timeKey, groupByRef.value);
      const data = rows.map((item) => ({
        title: item.title || t('unknown'),
        color: item.color || theme.value.seriesFallback,
        sort: item.sort,
        count: Number(item.count) || 0,
      }));
      tooltipCache.value.set(timeKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching tooltip data:', error);
      return [];
    }
  };

  // Debounced, and only re-dispatched when the pointer is still on the same bucket
  const debouncedFetchTooltipData = (timeKey) => {
    clearTimeout(tooltipDebounceTimer);
    tooltipDebounceTimer = setTimeout(async () => {
      if (tooltipCache.value.has(timeKey)) return;
      await fetchTooltipData(timeKey);

      if (chartRef.value?.chart && currentTooltipKey === timeKey && lastPointer) {
        chartRef.value.chart.dispatchAction({ type: 'showTip', x: lastPointer.x, y: lastPointer.y });
      }
    }, TOOLTIP_DEBOUNCE_MS);
  };

  const formatTooltipContent = (timeKey, data) => {
    let tooltip = `<div style="${TOOLTIP_WRAPPER}"><strong>${formatAxisLabel(timeKey)}</strong><br/>`;

    if (data && data.length > 0) {
      let total = 0;
      const sorted = [...data].sort((a, b) => {
        if (a.sort !== b.sort) return a.sort - b.sort;
        return String(a.title).localeCompare(String(b.title));
      });
      sorted.forEach((item) => {
        tooltip += `<span style="color:${item.color}">●</span> ${item.title}: ${item.count}<br/>`;
        total += item.count;
      });
      tooltip += `<strong>${t('total')}: ${total}</strong>`;
    } else {
      tooltip += t('noData');
    }

    return `${tooltip}</div>`;
  };

  // Opt-in `stacked: false`: the bucket label, then one row per band with its plotted value (ECharts'
  // params, in series order — a band hidden through the legend is not in them; a null value, a gap
  // in the line, reads "-" as in ECharts' own tooltip). Synchronous: no fetch, no cache, no total.
  const formatPlottedValues = (params) => {
    const list = Array.isArray(params) ? params : [params];
    const rows = list.map((point) => {
      const value = point.value === null || point.value === undefined || Number.isNaN(point.value) ? '-' : point.value;
      return `<span style="color:${point.color}">●</span> ${point.seriesName}: ${value}`;
    });
    return `<div style="${TOOLTIP_WRAPPER}"><strong>${formatAxisLabel(list[0]?.axisValue)}</strong><br/>`
      + `${rows.join('<br/>')}</div>`;
  };

  const tooltipFormatter = (params) => {
    if (!props.stacked) return formatPlottedValues(params);
    const timeKey = params[0].axisValue;
    currentTooltipKey = timeKey;

    if (!tooltipCache.value.has(timeKey)) {
      debouncedFetchTooltipData(timeKey);
      // The plotted values are already on screen: show their total while the real
      // breakdown loads, in BOTH modes (the burndown used to show a bare "Loading data…")
      const total = params.reduce((sum, series) => sum + (series.value || 0), 0);
      return `<div style="${TOOLTIP_WRAPPER}"><strong>${formatAxisLabel(timeKey)}</strong><br/>`
        + `<strong>${t('total')}: ${total}</strong></div>`;
    }

    return formatTooltipContent(timeKey, tooltipCache.value.get(timeKey));
  };

  // ---------------------------------------------------------------- option
  const updateChartOption = () => {
    const spec = modeSpec();
    const colours = theme.value;

    const xAxis = {
      type: 'category',
      data: chartData.value.timeData,
      axisLabel: { color: colours.fontColor, formatter: formatAxisLabel },
    };
    if (spec.boundaryGap !== null) xAxis.boundaryGap = spec.boundaryGap;

    const option = {
      tooltip: {
        trigger: 'axis',
        confine: true,
        enterable: true,
        extraCssText: 'max-width: 320px; overflow-x: auto;',
        axisPointer: {
          type: spec.axisPointer,
          label: { backgroundColor: colours.axisPointerLabel },
        },
        formatter: tooltipFormatter,
      },
      grid: { left: '3%', right: '4%', bottom: '12%', containLabel: true },
      xAxis,
      yAxis: {
        type: 'value',
        nameTextStyle: { color: colours.fontColor },
        axisLabel: { color: colours.fontColor },
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: 0,
          start: zoomStart.value,
          end: zoomEnd.value,
          height: 30,
          bottom: 10,
          borderColor: 'transparent',
          backgroundColor: colours.dataZoom.background,
          dataBackground: {
            lineStyle: { color: colours.dataZoom.dataBackground },
            areaStyle: { color: colours.dataZoom.dataBackground },
          },
          fillerColor: colours.dataZoom.filler,
          // ONE handle style for both modes — the ECharts default. The burndown's
          // custom `handleIcon` path is deliberately dropped.
          handleStyle: { color: colours.dataZoom.handle },
          labelFormatter: (value) => {
            const index = Math.floor(value);
            const keys = chartData.value.timeData;
            return index >= 0 && index < keys.length ? formatAxisLabel(keys[index]) : '';
          },
        },
        {
          type: 'inside',
          xAxisIndex: 0,
          start: zoomStart.value,
          end: zoomEnd.value,
          zoomOnMouseWheel: 'shift',
          moveOnMouseWheel: true,
          moveOnMouseMove: true,
        },
      ],
      series: chartData.value.seriesData,
    };

    // Opt-ins (AM_ABSORPTION_PLAN step D4) — keys added only when set, so the default option keeps
    // its exact shape. The marks hang off the first band (as AM hung them off one of its own), so
    // the option's series stay the bands; they are rebuilt here, with the theme's font colour, on
    // every repaint.
    if (props.showLegend) {
      option.legend = {
        top: 0,
        left: 'center',
        icon: 'roundRect',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: colours.fontColor },
      };
    }
    if (props.todayMarker || props.shadeFuture) option.series = withTodayMarks(option.series, colours);

    chartOption.value = option;
  };

  // The bucket holding today: the last key whose bucket starts on or before today (keys are sorted,
  // so this is AM's "the bucket before the first one after today"). Both marks need a LATER bucket
  // on the axis and today's on it — AM's `futureIndex > 0`: no future → no marks, and an axis that
  // starts after today has no today to mark.
  const withTodayMarks = (series, colours) => {
    const keys = chartData.value.timeData;
    const today = utcToday();
    let todayIndex = -1;
    keys.forEach((key, index) => {
      const start = bucketStartDate(groupByRef.value, key);
      if (start !== null && start <= today) todayIndex = index;
    });
    if (series.length === 0 || todayIndex < 0 || todayIndex >= keys.length - 1) return series;

    const marks = {};
    if (props.todayMarker) {
      marks.markLine = {
        silent: true,
        symbol: 'none',
        lineStyle: { color: colours.fontColor, opacity: 0.5, type: 'dashed' },
        label: { formatter: t('today'), color: colours.fontColor, fontSize: 11 },
        data: [{ xAxis: todayIndex }],
      };
    }
    if (props.shadeFuture) {
      marks.markArea = {
        silent: true,
        itemStyle: { color: colours.fontColor, opacity: 0.04 },
        data: [[{ xAxis: todayIndex }, { xAxis: keys.length - 1 }]],
      };
    }
    return [{ ...series[0], ...marks }, ...series.slice(1)];
  };

  // ---------------------------------------------------------------- data
  // Rows: { timeKey, color, value, sort }. `value` is plotted as-is — the config
  // decides whether that is a per-bucket count or a running total.
  const fetchAndBuild = async () => {
    const spec = modeSpec();
    const rows = await props.fetchSeries(groupByRef.value);
    const data = rows.map((row) => ({ ...row, color: row.color || theme.value.seriesFallback }));

    const timeKeys = [...new Set(data.map((row) => row.timeKey))].sort();
    const bands = new Map();

    data.forEach((row) => {
      if (!bands.has(row.color)) {
        bands.set(row.color, {
          ...bandSeries(spec, row.color),
          name: props.bandTitles?.[row.color] || row.color,
          data: new Array(timeKeys.length).fill(0),
          sort: row.sort,
        });
      }
      const band = bands.get(row.color);
      const index = timeKeys.indexOf(row.timeKey);
      if (index !== -1) band.data[index] = row.value;
    });

    chartData.value = {
      timeData: timeKeys,
      seriesData: [...bands.values()].sort(bandSort(spec)),
    };
    updateChartOption();
  };

  // Clear the tooltip cache and drop any zoom (R-A: before the new series arrive,
  // so a stale breakdown can never render under a new label), then fetch.
  const reload = async () => {
    tooltipCache.value = new Map();
    zoomStart.value = 0;
    zoomEnd.value = 100;
    isZoomed.value = false;

    if (isLoading.value) {
      pendingReload = true;
      return;
    }

    isLoading.value = true;
    try {
      await fetchAndBuild();
    } catch (error) {
      console.error('Error fetching time series data:', error);
    } finally {
      isLoading.value = false;
      if (pendingReload) {
        pendingReload = false;
        await reload();
      }
    }
  };

  watch(groupByRef, reload);
  // A theme toggle repaints the axes, the axis pointer and the slider — no reload
  watch(theme, updateChartOption);

  // ---------------------------------------------------------------- zoom
  const resetZoom = () => {
    zoomStart.value = 0;
    zoomEnd.value = 100;
    isZoomed.value = false;

    if (chartRef.value?.chart) {
      chartRef.value.chart.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
    }
  };

  const handleChartClick = () => {
    if (!chartRef.value?.chart) return;
    const option = chartRef.value.chart.getOption();

    if (option.dataZoom && option.dataZoom[0]) {
      const { start, end } = option.dataZoom[0];
      zoomStart.value = start;
      zoomEnd.value = end;
      isZoomed.value = start !== 0 || end !== 100;
    }
  };

  watch([zoomStart, zoomEnd], ([newStart, newEnd]) => {
    isZoomed.value = newStart !== 0 || newEnd !== 100;

    if (chartRef.value?.chart) {
      chartRef.value.chart.setOption({
        dataZoom: [
          { start: newStart, end: newEnd },
          { start: newStart, end: newEnd },
        ],
      });
    }
  });

  // ---------------------------------------------------------------- export
  const bandColour = (series) => series.itemStyle?.color || series.lineStyle?.color;

  const downloadChart = async () => {
    if (!chartRef.value) return;

    // Slice the visible range out of the zoom state
    const allTimeKeys = chartData.value.timeData || [];
    const totalPoints = allTimeKeys.length;
    let startIdx = Math.floor((zoomStart.value / 100) * totalPoints);
    let endIdx = Math.ceil((zoomEnd.value / 100) * totalPoints);
    if (startIdx >= endIdx) startIdx = Math.max(0, endIdx - 1);
    endIdx = Math.min(endIdx, totalPoints);

    const visibleTimeKeys = allTimeKeys.slice(startIdx, endIdx);

    // Only bands that actually have data in the visible range reach the legend
    const visibleSeries = chartData.value.seriesData.filter((series) => {
      if (!series.data) return false;
      for (let i = startIdx; i < endIdx && i < series.data.length; i += 1) {
        if (series.data[i] > 0) return true;
      }
      return false;
    });

    const visibleColors = new Set(
      visibleSeries.map((series) => bandColour(series)?.toLowerCase()).filter(Boolean),
    );

    const colorToTitles = new Map();
    const addTitle = (color, title) => {
      const key = color?.toLowerCase();
      if (!key || !title || !visibleColors.has(key)) return;
      if (!colorToTitles.has(key)) colorToTitles.set(key, new Set());
      colorToTitles.get(key).add(title);
    };

    if (props.fetchLegendTitles) {
      try {
        const titles = await props.fetchLegendTitles(visibleTimeKeys, groupByRef.value);
        titles.forEach((row) => addTitle(row.color, row.title));
      } catch (error) {
        console.error('Error fetching legend titles:', error);
      }
    } else {
      Object.entries(props.bandTitles ?? {}).forEach(([color, title]) => addTitle(color, title));
    }

    const seriesDataForLegend = visibleSeries.map((series) => {
      const color = bandColour(series);
      const matching = colorToTitles.get(color?.toLowerCase());
      const displayName = matching ? [...matching].join(', ') : series.name;
      return { name: displayName, titles: [displayName], color };
    });

    downloadChartAsPNG(chartRef.value.chart, props.exportFilename, seriesDataForLegend, {
      mode: theme.value.mode,
      title: props.exportTitle || props.title,
    });
  };

  // ---------------------------------------------------------------- lifecycle
  onMounted(() => {
    const chart = chartRef.value?.chart;
    if (chart) {
      chart.on('datazoom', (params) => {
        zoomStart.value = params.start;
        zoomEnd.value = params.end;
        isZoomed.value = params.start !== 0 || params.end !== 100;
      });
      // Where the pointer is, for the tooltip re-dispatch once a breakdown arrives
      chart.getZr().on('mousemove', (e) => {
        lastPointer = { x: e.offsetX, y: e.offsetY };
      });
      chart.getZr().on('globalout', () => {
        lastPointer = null;
        currentTooltipKey = null;
      });
    }
  });

  // Opt-in `resolveGroupBy` (issue-analysis's burndown picks its bucket from the data's date
  // span): awaited before the first load after `dataLoaded` turns true. A supported bucket
  // other than the current one is switched to — the bucket watch above then loads, so there
  // is still exactly one series query; anything else loads the current bucket. Filter ticks
  // never re-resolve; `dataLoaded` going false arms it again. Without the prop, `refresh`
  // calls `reload()` synchronously, i.e. exactly what the composable called before.
  let groupByResolved = false;
  watch(() => props.dataLoaded, (loaded) => { if (!loaded) groupByResolved = false; });
  const refresh = async () => {
    if (props.resolveGroupBy && !groupByResolved) {
      groupByResolved = true;
      let unit = null;
      try {
        unit = await props.resolveGroupBy();
      } catch (error) {
        console.error('Error resolving the time bucket:', error);
      }
      if (unit && unit !== groupByRef.value && props.groupByOptions.includes(unit)) {
        selectedGroupBy.value = unit;
        return;
      }
    }
    await reload();
  };

  // `filterContext` may be an ARRAY of contexts (issue-analysis's velocity reads two): the
  // chart then reloads on a tick from ANY of them. The composable watches one object's
  // `filtersLastUpdated` (deep), so an array goes in as one stand-in whose
  // `filtersLastUpdated` lists every context's tick ref — rebuilt only when the contexts
  // themselves change, so a page that re-renders a fresh array literal does not reload the
  // chart (the computed keeps returning the same stand-in, and Vue 3.4 does not re-run a
  // watcher whose computed source returned the same value). A single context passes through
  // untouched.
  let multiContext = null;
  const refreshContext = computed(() => {
    const ctx = props.filterContext;
    if (!Array.isArray(ctx)) return ctx;
    const ticks = ctx.map((c) => c?.filtersLastUpdated);
    const prev = multiContext?.filtersLastUpdated;
    if (!prev || prev.length !== ticks.length || ticks.some((tick, i) => tick !== prev[i])) {
      multiContext = { filtersLastUpdated: ticks };
    }
    return multiContext;
  });

  // Initial load once dataLoaded flips true, plus a reload on every filterContext
  // change. Registered AFTER the onMounted above, per the composable's rule.
  useFilterContextRefresh(
    refreshContext,
    computed(() => props.dataLoaded),
    refresh,
    { debounce: 0, immediate: true, requireDataLoaded: true },
  );

  return {
    // `chartRef` is a STRING template ref (`ref="chartRef"` in component.vue) — it only
    // binds because it is returned under that exact name.
    chartRef,
    chartOption,
    theme,
    t,
    selectedGroupBy,
    isZoomed,
    isLoading,
    showLoadingOverlay,
    resetZoom,
    handleChartClick,
    downloadChart,
    // The common chart-base name for the export (a page may call it through a template ref)
    downloadPng: downloadChart,
  };
};
